import Box from '@mui/material/Box';
import { t } from 'i18next';
import { useContext, useMemo } from 'react';

import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import { LoadsTableProps } from '~pages/Approvals/Files/components/LoadsTable/LoadsTable';

import ApproveChargesPageContext from '../ApproveChargesPageContext';
import { hidesAllRejectedLoads } from '../approveChargesTabs';
import ChargesBillingLinesQueryContext from '../ChargesBillingLinesQueryContext';
import {
  CHARGES_NESTED_HEADER_CELL_SX,
  chargesGridColumn,
  chargesNestedHeaderRowSx,
  chargesNestedHeaderTextSx,
} from '../chargesGrid';
import { isLoadAllRejected } from '../groupBillingLines';
import ChargesLoadRow from './ChargesLoadRow';

const loadHeaders = [
  {
    column: chargesGridColumn.time,
    title: () => t('approvals.approve_charges.columns.time'),
  },
  {
    column: chargesGridColumn.created,
    title: () => t('approvals.files.loads_table.headers.created'),
  },
  {
    column: chargesGridColumn.ticket,
    title: () => t('approvals.files.loads_table.headers.ticket'),
  },
  {
    column: chargesGridColumn.route,
    title: () => t('approvals.approve_charges.columns.pickup_to_dropoff'),
  },
  {
    column: chargesGridColumn.material,
    title: () => t('approvals.files.loads_table.headers.material'),
  },
  {
    column: chargesGridColumn.ticketQuantity,
    title: () => t('approvals.files.jobs_table.headers.total_quantity'),
  },
  {
    column: chargesGridColumn.payableAmount,
    title: () => t('approvals.files.jobs_table.headers.payables'),
  },
  {
    column: chargesGridColumn.receivableAmount,
    title: () => t('approvals.files.jobs_table.headers.receivables'),
  },
  {
    column: chargesGridColumn.payableNumber,
    title: () => t('approvals.approve_charges.columns.pay_number'),
  },
  {
    column: chargesGridColumn.invoiceNumber,
    title: () => t('approvals.approve_charges.columns.invoice_number'),
  },
];

export default function ChargesLoadsTable({
  job,
  loads,
  onTableRowClick,
  tickets,
}: LoadsTableProps) {
  const { chargesByJobId } = useContext(ChargesBillingLinesQueryContext);
  const { exception, showOption, viewTab } = useContext(ApproveChargesPageContext);
  const loadCharges = chargesByJobId.get(job.id)?.loads;

  const shownLoads = useMemo(
    () =>
      hidesAllRejectedLoads({ exception, showOption, viewTab })
        ? loads.filter((load) => !isLoadAllRejected(loadCharges?.get(load.id)))
        : loads,
    [exception, loadCharges, loads, showOption, viewTab],
  );

  return (
    <Box>
      <Box sx={chargesNestedHeaderRowSx}>
        {loadHeaders.map(({ column, title }) => (
          <Box key={column} sx={{ ...CHARGES_NESTED_HEADER_CELL_SX, gridColumn: column }}>
            <OverflowAwareText sx={chargesNestedHeaderTextSx}>
              {title()}
            </OverflowAwareText>
          </Box>
        ))}
      </Box>

      {shownLoads.map((load) => (
        <ChargesLoadRow
          key={load.id}
          charges={loadCharges?.get(load.id)}
          job={job}
          load={load}
          onClick={onTableRowClick}
          ticket={tickets.find(({ loadId }) => loadId === load.id)}
        />
      ))}
    </Box>
  );
}
